/**
 * Enhanced AdminStudentBulkUpload using the new component system
 * Demonstrates advanced bulk upload with file validation, progress tracking, and comprehensive error handling
 */

import React, { useEffect, useRef, useState } from 'react';
import { FiDownload, FiPause, FiPlay, FiUpload, FiX } from 'react-icons/fi';

// New component system imports
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Stack,
  HStack,
  List,
  ListIcon,
  ListItem,
  Progress,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AdminLayout,
  Card,
  ConfirmModal,
  Button as CustomButton,
  DataTable,
  TableColumn,
} from '@grcs/components';

// Existing hooks and types
import { useData } from '@grcs/context/DataContext';
import {
  useClassOperations,
  useSemesterOperations,
  useStudentOperations,
  useUserOperations,
} from '@grcs/hook/useDatabase';
import { ClassProfile } from '@grcs/types/DataTypes';
import { UserProfile, UserProfileForm, UserRole } from '@grcs/types/UserRole';
import { addNewStudent } from '@grcs/utils/users/addNewStudent';
import { getStudent } from '@grcs/utils/users/userManager';
import {
  normalizeStudentInput,
  validateStudentCSV,
} from '@grcs/utils/users/studentInput';
import SpreadsheetReader from '@grcs/views/shared/components/upload/SpreadsheetReader';

export enum UPLOAD_STATUS {
  PENDING = 'Pending',
  NEW_USER = 'New User',
  NEW_STUDENT = 'New Enrollment',
  NEW_CLASS = 'New Class',
  ENROLLED = 'Enrolled',
  ERROR = 'Error',
  PROCESSING = 'Processing',
  COMPLETED = 'Completed',
  SKIPPED = 'Skipped',
}

export interface UploadClassInterface {
  info: ClassProfile;
  status: UPLOAD_STATUS;
  error?: string;
}

export interface UploadStudentInterface {
  info: UserProfile;
  status: UPLOAD_STATUS;
  error?: string;
  row_number?: number;
}

interface BulkUploadStats {
  totalRecords: number;
  processedRecords: number;
  successfulRecords: number;
  errorRecords: number;
  newUsers: number;
  newStudents: number;
  newClasses: number;
  existingEnrollments: number;
}

interface UploadProgress {
  phase:
    | 'parsing'
    | 'validation'
    | 'classes'
    | 'users'
    | 'enrollment'
    | 'completed'
    | 'error';
  current: number;
  total: number;
  message: string;
}

export const AdminStudentBulkUploadEnhanced: React.FC = () => {
  const { supabase } = useData();
  const semesterOps = useSemesterOperations();
  const userOps = useUserOperations();
  const classOps = useClassOperations();
  const studentOps = useStudentOperations();
  const toast = useToast();

  const [currentYear, setCurrentYear] = useState<string>('');
  const [, setTeachers] = useState<UserProfile[]>([]);
  const [students, setStudents] = useState<
    Map<string, UploadStudentInterface[]>
  >(new Map());
  const [classes, setClasses] = useState<Map<string, UploadClassInterface>>(
    new Map(),
  );
  const [uploadCompleted, setUploadCompleted] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const pauseRef = useRef(false);
  const processingRef = useRef(false);
  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
      pauseRef.current = false;
    };
  }, []);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [progress, setProgress] = useState<UploadProgress>({
    phase: 'parsing',
    current: 0,
    total: 0,
    message: 'Ready to upload',
  });
  const [stats, setStats] = useState<BulkUploadStats>({
    totalRecords: 0,
    processedRecords: 0,
    successfulRecords: 0,
    errorRecords: 0,
    newUsers: 0,
    newStudents: 0,
    newClasses: 0,
    existingEnrollments: 0,
  });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // Load current semester and teachers with comprehensive error handling
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('🗓️ Loading current semester for Bulk Upload...');
        // Load current semester
        if (!currentYear) {
          const { data, error: semesterError } =
            await semesterOps.getCurrentSemesterYear();

          if (semesterError) {
            console.error('❌ Error fetching current semester:', semesterError);
            setError(
              'Failed to load semester information. Please try refreshing the page or contact your administrator.',
            );
            return;
          }

          if (data && data.length > 0) {
            const year = data[0].semester_year;
            console.log(`✅ Active semester found: ${year}`);
            setCurrentYear(year);
            setError(''); // Clear any previous errors
          } else {
            console.warn('⚠️ No active semester found');
            setError(
              'No active semester is currently set. Please go to Admin → Semester Management to create and activate a semester before uploading students.',
            );
            // Don't load teacher data if no active semester
            return;
          }
        }

        // Load teachers only if we have an active semester
        console.log('👥 Loading teacher data...');
        const { data: teacherData, error: teacherError } =
          await userOps.getUsersByRole(UserRole.TEACHER);
        if (teacherError) {
          console.error('❌ Error fetching teachers:', teacherError);
          throw new Error('Error fetching teachers');
        }
        if (teacherData && teacherData.length > 0) {
          setTeachers(teacherData as UserProfile[]);
          console.log(`✅ Loaded ${teacherData.length} teachers`);
        }
      } catch (error: any) {
        console.error('💥 Error loading data:', error);
        setError(
          error.message ||
            'Failed to load semester and teacher information. Please try refreshing the page.',
        );
        toast({
          title: 'Error Loading Data',
          description:
            error.message || 'Failed to load semester and teacher information',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    loadData();
  }, [currentYear, semesterOps, userOps, toast]);

  const handleDataParsed = async (parsedJson: any[]) => {
    if (processingRef.current) return;
    // Check for active semester before processing
    if (!currentYear || error) {
      console.log(
        '⏭️ Skipping CSV processing - no active semester or error present',
      );
      toast({
        title: 'No Active Semester',
        description:
          'Please ensure an active semester is set before uploading files.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const sanitizedRows = parsedJson.filter((row) =>
      row
        ? Object.values(row).some((value) => String(value ?? '').trim())
        : false,
    );

    const nonEmptyColumns = new Set<string>();
    sanitizedRows.forEach((row) => {
      Object.entries(row ?? {}).forEach(([key, value]) => {
        if (String(value ?? '').trim()) {
          nonEmptyColumns.add(key);
        }
      });
    });

    const cleanedRows = sanitizedRows.map((row) => {
      const cleanedRow: Record<string, any> = {};
      Object.entries(row ?? {}).forEach(([key, value]) => {
        if (nonEmptyColumns.has(key)) {
          cleanedRow[key] = value;
        }
      });
      return cleanedRow;
    });

    processingRef.current = true;
    setIsProcessing(true);
    setStats((prev) => ({
      ...prev,
      totalRecords: 0,
      processedRecords: 0,
      successfulRecords: 0,
      errorRecords: 0,
    }));
    setUploadCompleted(false);
    setStudents(new Map());
    setClasses(new Map());
    setProgress({
      phase: 'parsing',
      current: 0,
      total: cleanedRows.length,
      message: 'Parsing spreadsheet data...',
    });

    try {
      // Validate CSV data
      const validation = validateStudentCSV(cleanedRows);
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setProgress({
          phase: 'error',
          current: 0,
          total: 0,
          message: 'Validation failed',
        });
        toast({
          title: 'File Validation Failed',
          description: `Found ${validation.errors.length} validation error(s)`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      setValidationErrors([]);
      setProgress({
        phase: 'validation',
        current: 0,
        total: cleanedRows.length,
        message: 'Processing records...',
      });

      const tmp_classes: Map<string, UploadClassInterface> = new Map();
      const tmp_students: Map<string, UploadStudentInterface[]> = new Map();

      let newUsers = 0;
      let newStudents = 0;
      let newClasses = 0;
      let existingEnrollments = 0;

      for (let i = 0; i < cleanedRows.length; i++) {
        const item: Record<string, any> = {
          ...cleanedRows[i],
          class: cleanedRows[i].class.trim(),
        };
        setProgress({
          phase: 'validation',
          current: i + 1,
          total: cleanedRows.length,
          message: `Processing record ${i + 1}...`,
        });

        // Process class
        if (item.class) {
          if (!tmp_classes.has(item.class)) {
            let class_info = {
              class_name: item.class,
              semester_year: currentYear,
            } as ClassProfile;
            let class_status = UPLOAD_STATUS.PENDING;

            const class_entry = await classOps.getClassByNameAndSemester(
              item.class,
              currentYear,
            );
            if (
              class_entry.error ||
              !class_entry.data ||
              class_entry.data.length > 1
            )
              throw new Error(
                `Could not resolve class ${item.class}. Check for lookup errors or duplicate classes.`,
              );
            if (class_entry.data.length > 0) {
              class_info = class_entry.data[0];
              class_status = UPLOAD_STATUS.ENROLLED;
            } else {
              newClasses++;
            }

            tmp_classes.set(item.class, {
              info: class_info,
              status: class_status,
            });
            tmp_students.set(item.class, []);
          }

          // Process student
          const studentUser: UserProfileForm = normalizeStudentInput({
            first_name: item.first_name,
            last_name: item.last_name,
            email: item.email,
            dob: item.dob,
          });

          try {
            const studentUserInfo = await getStudent(studentUser, supabase);
            if (studentUserInfo.user === null) {
              tmp_students.get(item.class)?.push({
                info: studentUser as UserProfile,
                status: UPLOAD_STATUS.NEW_USER,
                row_number: i + 1,
              });
              newUsers++;
            } else {
              // Check if student exists in current semester specifically
              const semesterStudentInfo = await studentOps.getStudentBySemester(
                studentUserInfo.user.user_token,
                currentYear,
              );

              if (
                semesterStudentInfo.error ||
                !semesterStudentInfo.data ||
                semesterStudentInfo.data.length > 1
              )
                throw new Error('Could not resolve student enrollment.');
              const enrolled = semesterStudentInfo.data.length === 1;
              tmp_students.get(item.class)?.push({
                // Retain submitted details; the lookup only supplies identity/status.
                info: { ...studentUserInfo.user, ...studentUser },
                status: enrolled
                  ? UPLOAD_STATUS.ENROLLED
                  : UPLOAD_STATUS.NEW_STUDENT,
                row_number: i + 1,
              });
              if (enrolled) existingEnrollments++;
              else newStudents++;
            }
          } catch (error) {
            tmp_students.get(item.class)?.push({
              info: studentUser as UserProfile,
              status: UPLOAD_STATUS.ERROR,
              error: `Failed to process student: ${error}`,
              row_number: i + 1,
            });
          }
        }
      }

      const previewErrors = Array.from(tmp_students.values())
        .flat()
        .filter((row) => row.status === UPLOAD_STATUS.ERROR).length;
      setStudents(tmp_students);
      setClasses(tmp_classes);
      setStats({
        totalRecords: cleanedRows.length,
        processedRecords: 0,
        successfulRecords: 0,
        errorRecords: previewErrors,
        newUsers,
        newStudents,
        newClasses,
        existingEnrollments,
      });

      setProgress({
        phase: 'completed',
        current: cleanedRows.length,
        total: cleanedRows.length,
        message: 'Processing completed',
      });

      toast({
        title: 'File Preview Ready',
        description: `Previewed ${cleanedRows.length} records; ${previewErrors} need attention.`,
        status: previewErrors ? 'warning' : 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Error processing CSV:', error);
      setProgress({
        phase: 'error',
        current: 0,
        total: 0,
        message: 'Processing failed',
      });
      toast({
        title: 'Error Processing File',
        description: error.message || 'Failed to process uploaded file',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      processingRef.current = false;
      setIsProcessing(false);
    }
  };

  const handleStartUpload = () => {
    if (!currentYear) {
      toast({
        title: 'No Active Semester',
        description:
          'Please ensure an active semester is set before uploading students.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    setIsConfirmOpen(true);
  };

  const handleConfirmUpload = async () => {
    setIsConfirmOpen(false);
    if (processingRef.current || !currentYear) return;
    processingRef.current = true;
    pauseRef.current = false;
    setIsProcessing(true);
    setIsPaused(false);
    setProgress({
      phase: 'classes',
      current: 0,
      total: classes.size,
      message: 'Preparing classes...',
    });
    const waitWhilePaused = async () => {
      while (pauseRef.current && !cancelledRef.current)
        await new Promise((resolve) => setTimeout(resolve, 150));
      if (cancelledRef.current)
        throw new Error('Upload stopped because the page was closed.');
    };
    const workingClasses = new Map(
      Array.from(classes, ([key, value]) => [key, { ...value }]),
    );
    const workingStudents = new Map(
      Array.from(students, ([key, list]) => [
        key,
        list.map((row) => ({ ...row })),
      ]),
    );
    const total = Array.from(workingStudents.values()).flat().length;
    let processed = 0;
    let succeeded = 0;
    let failed = 0;
    try {
      const active = await semesterOps.getCurrentSemesterYear();
      if (
        active.error ||
        active.data?.length !== 1 ||
        active.data[0].semester_year !== currentYear
      )
        throw new Error(
          'The active semester changed or is unavailable. Reload before uploading.',
        );
      let classCount = 0;
      for (const [name, entry] of workingClasses) {
        await waitWhilePaused();
        try {
          // Recheck at write time: the preview may be stale.
          let result = await classOps.getClassByNameAndSemester(
            name,
            currentYear,
          );
          if (result.error || !result.data || result.data.length > 1)
            throw new Error('Class lookup failed or is ambiguous');
          if (!result.data.length)
            result = await classOps.addNewClassByNameAndSemester(
              name,
              currentYear,
              entry.info.teacher_token,
            );
          if (
            result.error ||
            result.data?.length !== 1 ||
            !result.data[0].class_token
          )
            throw new Error('Class creation failed');
          workingClasses.set(name, {
            info: result.data[0] as ClassProfile,
            status: UPLOAD_STATUS.ENROLLED,
          });
        } catch (error) {
          workingClasses.set(name, {
            ...entry,
            status: UPLOAD_STATUS.ERROR,
            error:
              error instanceof Error
                ? error.message
                : 'Class processing failed',
          });
        }
        classCount++;
        setProgress({
          phase: 'classes',
          current: classCount,
          total: workingClasses.size,
          message: `Processed class ${classCount} of ${workingClasses.size}`,
        });
        setClasses(new Map(workingClasses));
      }
      for (const [name, rows] of workingStudents) {
        for (const row of rows) {
          await waitWhilePaused();
          try {
            const target = workingClasses.get(name);
            if (
              target?.status !== UPLOAD_STATUS.ENROLLED ||
              !target.info.class_token
            )
              throw new Error(
                'Class could not be prepared; student enrollment was not attempted.',
              );
            const result = await addNewStudent(
              row.info,
              supabase,
              currentYear,
              target.info.class_token,
              name,
            );
            if (result.error || !result.data?.length)
              throw new Error('Student enrollment could not be confirmed');
            row.info = result.data[0];
            row.status = UPLOAD_STATUS.COMPLETED;
            row.error = undefined;
            succeeded++;
          } catch (error) {
            row.status = UPLOAD_STATUS.ERROR;
            row.error =
              error instanceof Error
                ? error.message
                : 'Student processing failed';
            failed++;
          }
          processed++;
          setStudents(new Map(workingStudents));
          const counts = {
            processedRecords: processed,
            successfulRecords: succeeded,
            errorRecords: failed,
          };
          setStats((prev) => ({ ...prev, ...counts }));
          setProgress({
            phase: 'users',
            current: processed,
            total,
            message: `Processed ${processed} of ${total} students`,
          });
        }
      }
      setUploadCompleted(true);
      setProgress({
        phase: 'completed',
        current: total,
        total,
        message: `Upload finished: ${succeeded} succeeded, ${failed} failed.`,
      });
      toast({
        title: failed ? 'Upload Finished with Errors' : 'Bulk Upload Complete',
        description: `${succeeded} succeeded; ${failed} failed.`,
        status: failed ? 'warning' : 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      if (cancelledRef.current) return;
      const message = error instanceof Error ? error.message : 'Upload failed';
      setProgress({ phase: 'error', current: processed, total, message });
      toast({
        title: 'Upload Error',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      processingRef.current = false;
      pauseRef.current = false;
      if (!cancelledRef.current) {
        setIsPaused(false);
        setIsProcessing(false);
      }
    }
  };

  const handlePauseResume = () => {
    pauseRef.current = !pauseRef.current;
    setIsPaused(pauseRef.current);
  };

  const downloadTemplate = () => {
    const template =
      'first_name,last_name,email,dob,class\nJohn,Doe,john.doe@grcs.com,2010/01/01,ClassA\nJane,Smith,jane.smith@grcs.com,2010/02/15,ClassB';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_upload_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getPhaseProgress = () => {
    if (progress.total === 0) return 0;
    return (progress.current / progress.total) * 100;
  };

  const getAllStudents = (): UploadStudentInterface[] => {
    const allStudents: UploadStudentInterface[] = [];
    students.forEach((studentList) => {
      allStudents.push(...studentList);
    });
    return allStudents;
  };

  const getAllClasses = (): UploadClassInterface[] => {
    return Array.from(classes.values());
  };

  // Table columns for students
  const studentColumns: TableColumn<UploadStudentInterface>[] = [
    {
      key: 'row_number',
      header: 'Row',
      render: (student: UploadStudentInterface, value: number) => (
        <Text fontSize="sm" fontWeight="500">
          #{value}
        </Text>
      ),
    },
    {
      key: 'info',
      header: 'Student',
      render: (student: UploadStudentInterface, value: UserProfile) => (
        <VStack spacing={1} align="start">
          <Text fontSize="sm" fontWeight="600">
            {value.first_name} {value.last_name}
          </Text>
          <Text fontSize="xs" color="gray.600">
            {value.email}
          </Text>
        </VStack>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (student: UploadStudentInterface, value: UPLOAD_STATUS) => (
        <VStack spacing={1} align="start">
          <Badge
            colorScheme={
              value === UPLOAD_STATUS.COMPLETED
                ? 'green'
                : value === UPLOAD_STATUS.ERROR
                ? 'red'
                : value === UPLOAD_STATUS.PROCESSING
                ? 'blue'
                : 'gray'
            }
            size="sm"
          >
            {value}
          </Badge>
          {student.error && (
            <Text fontSize="xs" color="red.600">
              {student.error}
            </Text>
          )}
        </VStack>
      ),
    },
  ];

  // Table columns for classes
  const classColumns: TableColumn<UploadClassInterface>[] = [
    {
      key: 'info',
      header: 'Class Name',
      render: (classItem: UploadClassInterface, value: ClassProfile) => (
        <Text fontSize="sm" fontWeight="600">
          {value.class_name}
        </Text>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (classItem: UploadClassInterface, value: UPLOAD_STATUS) => (
        <VStack spacing={1} align="start">
          <Badge
            colorScheme={
              value === UPLOAD_STATUS.ENROLLED
                ? 'green'
                : value === UPLOAD_STATUS.ERROR
                ? 'red'
                : value === UPLOAD_STATUS.PENDING
                ? 'yellow'
                : 'gray'
            }
            size="sm"
          >
            {value === UPLOAD_STATUS.ENROLLED
              ? 'Exists'
              : value === UPLOAD_STATUS.PENDING
              ? 'Will Create'
              : value}
          </Badge>
          {classItem.error && (
            <Text fontSize="xs" color="red.600">
              {classItem.error}
            </Text>
          )}
        </VStack>
      ),
    },
  ];

  const hasPreview = classes.size > 0 || students.size > 0;
  const canUpload =
    classes.size > 0 &&
    students.size > 0 &&
    !uploadCompleted &&
    !isProcessing &&
    validationErrors.length === 0;
  const progressColor =
    progress.phase === 'error'
      ? 'red'
      : isPaused || (uploadCompleted && stats.errorRecords > 0)
      ? 'orange'
      : uploadCompleted
      ? 'green'
      : 'blue';
  const summary = uploadCompleted
    ? [
        { label: 'Total students', value: stats.totalRecords },
        { label: 'Succeeded', value: stats.successfulRecords },
        { label: 'Failed', value: stats.errorRecords },
        { label: 'Classes', value: classes.size },
      ]
    : [
        { label: 'Total students', value: stats.totalRecords },
        { label: 'New accounts', value: stats.newUsers },
        { label: 'New classes', value: stats.newClasses },
        { label: 'Errors', value: stats.errorRecords },
      ];

  return (
    <>
      <AdminLayout
        header={{
          title: 'Students Upload',
          subtitle: `${currentYear}`,
          breadcrumb: {
            items: [
              { label: 'Admin', href: '/admin' },
              { label: 'Students', href: '/admin/student' },
              { label: 'Bulk Upload', isCurrent: true },
            ],
          },
        }}
      >
        <VStack spacing={4} align="stretch">
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Card padding={5}>
            <VStack spacing={4} align="stretch">
              <Stack
                direction={{ base: 'column', sm: 'row' }}
                justify="space-between"
                align={{ base: 'stretch', sm: 'center' }}
                spacing={3}
              >
                <Box>
                  <Text as="h2" fontSize="md" fontWeight="semibold">
                    1. Select a CSV or Excel file
                  </Text>
                </Box>
                <CustomButton
                  variant="secondary"
                  size="sm"
                  onClick={downloadTemplate}
                >
                  <FiDownload size={14} /> Download CSV Template
                </CustomButton>
              </Stack>
              <HStack spacing={2} flexWrap="wrap" rowGap={2}>
                <Text fontSize="xs" color="gray.600">
                  Required columns:
                </Text>
                {['first_name', 'last_name', 'email', 'dob', 'class'].map(
                  (column) => (
                    <Badge
                      key={column}
                      textTransform="none"
                      fontFamily="mono"
                      fontWeight="normal"
                      px={2}
                      borderRadius="sm"
                    >
                      {column}
                    </Badge>
                  ),
                )}
              </HStack>
              {!isProcessing ? (
                <SpreadsheetReader
                  onDataParsed={handleDataParsed}
                  onFileChange={() => {
                    setStudents(new Map());
                    setClasses(new Map());
                    setValidationErrors([]);
                    setUploadCompleted(false);
                    setIsConfirmOpen(false);
                  }}
                />
              ) : (
                <Box
                  borderWidth="1px"
                  borderStyle="dashed"
                  borderRadius="md"
                  px={4}
                  py={5}
                >
                  <Text fontSize="sm" color="gray.600">
                    File selection is available when processing finishes.
                  </Text>
                </Box>
              )}
              <Text fontSize="xs" color="gray.600">
                DOB: YYYY-MM-DD, YYYY/MM/DD, or an Excel date cell. New student
                passwords default to their birthday (YYYYMMDD).
              </Text>
            </VStack>
          </Card>

          {validationErrors.length > 0 && (
            <Alert
              status="error"
              variant="left-accent"
              alignItems="flex-start"
              borderRadius="md"
            >
              <AlertIcon mt={1} />
              <Box minW={0}>
                <AlertTitle fontSize="sm">
                  Review {validationErrors.length} validation error(s)
                </AlertTitle>
                <Text fontSize="sm" mb={2}>
                  Correct the file and select it again before uploading.
                </Text>
                <List spacing={1} maxH="160px" overflowY="auto">
                  {validationErrors.slice(0, 20).map((message, index) => (
                    <ListItem key={index} fontSize="sm">
                      <ListIcon as={FiX} color="red.500" />
                      {message}
                    </ListItem>
                  ))}
                  {validationErrors.length > 20 && (
                    <ListItem fontSize="sm">
                      And {validationErrors.length - 20} more errors.
                    </ListItem>
                  )}
                </List>
              </Box>
            </Alert>
          )}

          {(isProcessing || uploadCompleted || progress.phase === 'error') && (
            <Card padding={4} variant="filled">
              <VStack spacing={3} align="stretch">
                <Stack
                  direction={{ base: 'column', sm: 'row' }}
                  justify="space-between"
                  align={{ base: 'stretch', sm: 'center' }}
                  spacing={2}
                >
                  <HStack spacing={2} flexWrap="wrap">
                    <Text as="h2" fontSize="sm" fontWeight="semibold">
                      Upload Progress
                    </Text>
                    <Badge colorScheme={progressColor} borderRadius="sm">
                      {progress.phase === 'error'
                        ? 'Needs attention'
                        : isPaused
                        ? 'Paused'
                        : uploadCompleted
                        ? stats.errorRecords
                          ? 'Finished with errors'
                          : 'Completed'
                        : 'Processing'}
                    </Badge>
                  </HStack>
                  {isProcessing &&
                    ['classes', 'users'].includes(progress.phase) && (
                      <CustomButton
                        variant="secondary"
                        size="sm"
                        onClick={handlePauseResume}
                      >
                        {isPaused ? (
                          <FiPlay size={14} />
                        ) : (
                          <FiPause size={14} />
                        )}
                        {isPaused ? 'Resume' : 'Pause'}
                      </CustomButton>
                    )}
                </Stack>
                <Progress
                  value={getPhaseProgress()}
                  colorScheme={progressColor}
                  size="sm"
                  borderRadius="full"
                  aria-label="Upload progress"
                />
                <Text fontSize="sm" color="gray.600" role="status">
                  {progress.message}
                </Text>
              </VStack>
            </Card>
          )}

          {uploadCompleted && (
            <Alert
              status={stats.errorRecords ? 'warning' : 'success'}
              variant="left-accent"
              borderRadius="md"
            >
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">
                  {stats.errorRecords
                    ? 'Upload Finished with Errors'
                    : 'Upload Completed Successfully!'}
                </AlertTitle>
                <AlertDescription fontSize="sm">
                  {stats.successfulRecords} student records succeeded;{' '}
                  {stats.errorRecords} failed.
                  {stats.errorRecords > 0 &&
                    ' Review row errors below before retrying failed records.'}
                </AlertDescription>
              </Box>
            </Alert>
          )}

          {hasPreview && (
            <Card padding={5}>
              <VStack spacing={4} align="stretch">
                <Stack
                  direction={{ base: 'column', sm: 'row' }}
                  justify="space-between"
                  align={{ base: 'stretch', sm: 'center' }}
                  spacing={3}
                >
                  <Box>
                    <Text as="h2" fontSize="md" fontWeight="semibold">
                      {uploadCompleted
                        ? '2. Review results'
                        : '2. Review and upload'}
                    </Text>
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      {uploadCompleted
                        ? 'Check each student’s final status below.'
                        : 'Review student details and class assignments before confirming.'}
                    </Text>
                  </Box>
                  {canUpload && (
                    <CustomButton
                      variant="primary"
                      size="sm"
                      onClick={handleStartUpload}
                    >
                      <FiUpload size={14} /> Start Bulk Upload
                    </CustomButton>
                  )}
                </Stack>
                <SimpleGrid
                  columns={{ base: 2, md: 4 }}
                  spacing={3}
                  bg="gray.50"
                  borderRadius="md"
                  p={3}
                >
                  {summary.map((item) => (
                    <Box key={item.label}>
                      <Text fontSize="xs" color="gray.600">
                        {item.label}
                      </Text>
                      <Text
                        fontSize="xl"
                        fontWeight="semibold"
                        color={
                          (item.label === 'Errors' ||
                            item.label === 'Failed') &&
                          item.value > 0
                            ? 'red.600'
                            : undefined
                        }
                      >
                        {item.value}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
                <Tabs size="sm" colorScheme="blue" isLazy>
                  <TabList>
                    <Tab>Students ({getAllStudents().length})</Tab>
                    <Tab>Classes ({getAllClasses().length})</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel px={0} pb={0}>
                      <Box overflowX="auto">
                        <DataTable
                          data={getAllStudents()}
                          columns={studentColumns}
                          emptyState={
                            <Text fontSize="sm" color="gray.500" py={4}>
                              No student records to display.
                            </Text>
                          }
                        />
                      </Box>
                    </TabPanel>
                    <TabPanel px={0} pb={0}>
                      <Box overflowX="auto">
                        <DataTable
                          data={getAllClasses()}
                          columns={classColumns}
                          emptyState={
                            <Text fontSize="sm" color="gray.500" py={4}>
                              No classes to display.
                            </Text>
                          }
                        />
                      </Box>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </VStack>
            </Card>
          )}
        </VStack>
      </AdminLayout>

      <ConfirmModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        type="warning"
        title="Confirm Bulk Upload"
        message={`You are about to upload ${stats.totalRecords} student records. This will create ${stats.newUsers} new users and ${stats.newClasses} new classes.`}
        description="This action cannot be undone. Please ensure all data is correct before proceeding."
        onConfirm={handleConfirmUpload}
        confirmText="Start Upload"
      />
    </>
  );
};
